import React, { useState } from "react";
import { db, auth, storage } from "./firebase";
import { collection, doc, setDoc, addDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { Timestamp } from "firebase/firestore";
import { useAdmin } from "./hooks/useAdmin";

// ─── Points formula (matches existing Flymark / Contaste) ─────────────────────
function calculatePoints(place, participants) {
  const raw =
    321 * Math.exp(-0.077 * place) * (0.39 * Math.log(participants) + 0.13);
  return Math.round(raw);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function sanitizeId(str) {
  return (str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function standardizeDance(text) {
  const t = (text || "").trim();
  const map = {
    "Sa": "Samba", "Samba": "Samba",
    "Ch": "Cha Cha Cha", "Cha Cha": "Cha Cha Cha", "Cha Cha Cha": "Cha Cha Cha",
    "Ru": "Rumba", "Rumba": "Rumba",
    "Pd": "Paso Doble", "Paso Doble": "Paso Doble", "PasoDoble": "Paso Doble",
    "Ji": "Jive", "Jive": "Jive",
    "SW": "Slow Waltz", "Slow Waltz": "Slow Waltz", "SlowWaltz": "Slow Waltz",
    "Ta": "Tango", "Tango": "Tango",
    "VW": "Viennese Waltz", "Viennese Waltz": "Viennese Waltz", "VienneseWaltz": "Viennese Waltz",
    "SF": "Slow Foxtrot", "Slow Foxtrot": "Slow Foxtrot", "SlowFoxtrot": "Slow Foxtrot",
    "QS": "Quickstep", "Quickstep": "Quickstep",
    "Co": "Contemporary", "Contemporary": "Contemporary",
    "HH": "Hip Hop", "Hip Hop": "Hip Hop", "HipHop": "Hip Hop",
    "Comm": "Commercial", "Commercial": "Commercial",
  };
  if (map[t]) return map[t];
  const lower = t.toLowerCase().replace(/\s+/g, " ");
  for (const [key, val] of Object.entries(map)) {
    if (lower === key.toLowerCase()) return val;
  }
  // Multi-dance: "Sa Ch Ru Pd Ji" → keep as joined standardized dances
  const parts = t.split(/\s+/).map(p => map[p] || p).filter(Boolean);
  if (parts.length > 1) return parts.join(" / ");
  return t || "Dance";
}

function standardizeCategory(text) {
  const t = (text || "").trim();
  const replacements = [
    [/\bJuniors?\s*1\b/i, "Juniors I"],
    [/\bJuniors?\s*2\b/i, "Juniors II"],
    [/\bJuniors?\s*3\b/i, "Juniors III"],
    [/\bJuveniles?\s*1\b/i, "Juvenile I"],
    [/\bJuveniles?\s*2\b/i, "Juvenile II"],
    [/\bAdult\b/i, "Adult"],
    [/\bSeniors?\s*1\b/i, "Senior I"],
    [/\bSeniors?\s*2\b/i, "Senior II"],
    [/\bPre[\s-]?Juniors?\b/i, "Pre Juniors"],
    [/\bPre[\s-]?Juveniles?\b/i, "Pre Juvenile"],
    [/\bMini[\s-]?Juniors?\b/i, "Mini Juniors"],
    [/\bMini[\s-]?Juveniles?\b/i, "Mini Juvenile"],
    [/\bPro[\s-]?Am\b/i, "Pro-Am"],
  ];
  let result = t;
  for (const [pattern, replacement] of replacements) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

function detectDiscipline(dance) {
  const d = (dance || "").toLowerCase();
  if (/samba|cha cha|rumba|paso|jive/.test(d)) return "Latin";
  if (/waltz|tango|foxtrot|quickstep/.test(d)) return "Ballroom";
  if (/hip.?hop/.test(d)) return "Hip Hop";
  if (/contemporary/.test(d)) return "Contemporary";
  if (/commercial/.test(d)) return "Commercial";
  return "Latin";
}

// ─── Expand round label abbreviations ─────────────────────────────────────────
function expandRoundLabel(abbr) {
  const t = (abbr || "").trim();
  if (t === "F") return "F";
  if (t === "1/2F" || t === "1/2 F") return "1/2 Final";
  if (t === "1/4F" || t === "1/4 F") return "1/4 Final";
  if (t === "1/8F" || t === "1/8 F") return "1/8 Final";
  if (t === "1/16F" || t === "1/16 F") return "1/16 Final";
  if (t === "1/32F" || t === "1/32 F") return "1/32 Final";
  return t;
}

// ─── Round sort order (same as Contaste) ──────────────────────────────────────
function roundOrder(r) {
  if (r === "F") return 10000;
  if (r === "Preliminary") return -1;
  const frac = { "1/32 Final": 10, "1/16 Final": 20, "1/8 Final": 30, "1/4 Final": 40, "1/2 Final": 50 };
  if (frac[r] != null) return frac[r];
  const n = parseInt(r);
  return isNaN(n) ? 5 : n;
}

// ─── Parse the main Flymark section ───────────────────────────────────────────
// Returns: { competitionName, categoryRaw, danceRaw, adjudicators, participants, results }
//   adjudicators: [{ id, name, country, rounds: ['1/8 Final', 'F', ...] }]
//   results: [{ number, dancerName, studio, place, points }]
function parseFlymarkMain(text) {
  const lines = text.split(/\r?\n/);
  let idx = 0;

  // Skip blank lines at top
  while (idx < lines.length && !lines[idx].trim()) idx++;

  // Line 1: competition name
  const competitionName = lines[idx]?.trim() || "";
  idx++;

  // Line 2: "Category - Dance" (may have blank line before it)
  while (idx < lines.length && !lines[idx].trim()) idx++;
  const headerLine = lines[idx]?.trim() || "";
  idx++;

  // Parse "Juniors 1 Solo - Sa" or "Adult Couple - Ch Ru Pd Ji"
  let categoryRaw = "";
  let danceRaw = "";
  const dashIdx = headerLine.lastIndexOf(" - ");
  if (dashIdx !== -1) {
    // Strip "Solo" / "Couple" type indicator from category part
    let catPart = headerLine.substring(0, dashIdx).trim();
    catPart = catPart.replace(/\s+(Solo|Couple|Duo|Group|Formation|Cabaret|Showdance|Show Dance)$/i, "").trim();
    categoryRaw = catPart;
    danceRaw = headerLine.substring(dashIdx + 3).trim();
  } else {
    categoryRaw = headerLine;
  }

  // Parse judges section
  // Format per judge (multi-line block):
  //   A.
  //   Judge Name
  //   Country
  //   ( 1/8F, F)
  const adjudicators = [];

  // Scan forward to find "Judges" header
  while (idx < lines.length && !/^judges?$/i.test(lines[idx]?.trim())) idx++;
  idx++; // skip "Judges" header

  while (idx < lines.length) {
    const line = lines[idx]?.trim();

    // Stop when we hit the "Entries:" summary line or the table header
    if (/^Entries?:/i.test(line) || /^№/.test(line)) break;

    // Judge letter ID line: "A." or "B." etc.
    if (/^[A-Z]\.$/.test(line)) {
      const judgeId = line.replace(".", "");
      idx++;
      const judgeName = lines[idx]?.trim() || "";
      idx++;
      const judgeCountry = lines[idx]?.trim() || "";
      idx++;
      // Rounds line: "( 1/8F, F)" — only present in some Flymark exports.
      // Peek ahead and only consume the line if it contains parentheses;
      // otherwise the next judge's letter would be swallowed and every
      // other judge silently dropped.
      const peekLine = lines[idx]?.trim() || "";
      const rounds = [];
      if (peekLine.includes("(") && peekLine.includes(")")) {
        idx++; // consume rounds line
        const roundsMatch = peekLine.match(/\(([^)]+)\)/);
        if (roundsMatch) {
          const parts = roundsMatch[1].split(",").map(s => expandRoundLabel(s.trim())).filter(Boolean);
          rounds.push(...parts);
        }
      }
      adjudicators.push({ id: judgeId, name: judgeName, country: judgeCountry, rounds });
      continue;
    }
    idx++;
  }

  // Skip "Entries:X Clubs:Y Cities:Z" line
  while (idx < lines.length && !/^№/.test(lines[idx]?.trim())) idx++;

  // Table header row: "№\tPlace\tParticipant 1\tCity\tClub\tTrainer"
  idx++; // skip header

  // Parse participant rows
  const rawResults = [];
  while (idx < lines.length) {
    const line = lines[idx]?.trim();
    idx++;
    if (!line) continue;

    const cols = line.split("\t");
    if (cols.length < 3) continue;

    const number = parseInt(cols[0]);
    if (isNaN(number)) continue;

    // Place can be "1", "9 - 11", "—", or blank (DNS/DNF)
    const placeStr = (cols[1] || "").trim();
    let place = 0;
    if (/^\d+$/.test(placeStr)) {
      place = parseInt(placeStr);
    } else if (/^(\d+)\s*[-–]\s*(\d+)$/.test(placeStr)) {
      place = parseInt(placeStr.split(/[-–]/)[0].trim());
    }

    const dancerName = (cols[2] || "").trim();
    const city = (cols[3] || "").trim();   // City column (index 3)
    const club = (cols[4] || "").trim();   // Club column (index 4)
    // Use Club if it has a real value, otherwise fall back to City
    const studio = (club && club !== "-") ? club : (city && city !== "-" ? city : "");

    if (!dancerName) continue;

    rawResults.push({ number, place, dancerName, studio });
  }

  // Assign consecutive places for tied ranges (e.g. "9 - 11" → all get place 9)
  // Sort by place first (0-place entries go last)
  rawResults.sort((a, b) => {
    if (a.place === 0 && b.place === 0) return 0;
    if (a.place === 0) return 1;
    if (b.place === 0) return -1;
    return a.place - b.place;
  });

  const participants = rawResults.filter(r => r.place > 0).length;

  const results = rawResults.map(r => ({
    ...r,
    points: r.place > 0 ? calculatePoints(r.place, participants) : 0,
  }));

  return { competitionName, categoryRaw, danceRaw, adjudicators, participants, results };
}

// ─── Parse a single round's detail section ────────────────────────────────────
// Handles two formats:
//
// PRELIMINARY (X/- recall marks):
//   №\tSum\tPlace\tS
//   ABCDEFGHI            ← all judge letters concatenated, no tabs
//   360\t9\t1 - 9\tXXXXXXXXX
//
// FINAL (numeric placement marks, tab-separated per judge):
//   S
//   №\tA\tB\tC\tD\tE\tF\tG\tH\tI
//   1120\t3\t6\t4\t1\t2\t1\t1\t1\t6
//
// Returns: { roundLabel, isFinal, judgeLetters, competitors }
//   competitors: [{ number, crossSum, placeRange, judgeRecalls, judgeMarks }]
function parseFlymarkRound(text, manualRoundLabel, positionFallback) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
  if (lines.length === 0) return null;

  // Auto-detect round label from first line(s)
  let roundLabel = null;
  let dataStartIdx = 0;

  for (let i = 0; i < Math.min(5, lines.length); i++) {
    const expanded = expandRoundLabel(lines[i]);
    if (["F", "1/2 Final", "1/4 Final", "1/8 Final", "1/16 Final", "1/32 Final"].includes(expanded)) {
      roundLabel = expanded;
      dataStartIdx = i + 1;
      break;
    }
  }

  if (manualRoundLabel) roundLabel = expandRoundLabel(manualRoundLabel);
  if (!roundLabel) roundLabel = positionFallback ? expandRoundLabel(positionFallback) : "F";

  // ── Detect format ──────────────────────────────────────────────────────────
  // Final format: has a header row like "№\tA\tB\tC\t..." (tab-separated single letters)
  // Preliminary format: has a concatenated letters row like "ABCDEFGHI" after the №\tSum\tPlace\tS header

  let isFinalFormat = false;
  let headerIdx = -1;       // row with № (the main column header)
  let judgeLettersRow = []; // ordered list of judge letters

  // Scan for the № header row
  for (let i = dataStartIdx; i < lines.length; i++) {
    if (!/^№/.test(lines[i])) continue;
    const cols = lines[i].split("\t").map(c => c.trim());
    // Final format: "№\tA\tB\tC\t..." — second column is a single letter
    if (cols.length >= 3 && /^[A-Z]$/.test(cols[1])) {
      isFinalFormat = true;
      headerIdx = i;
      judgeLettersRow = cols.slice(1); // ["A","B","C",...]
      break;
    }
    // Preliminary format: "№\tSum\tPlace\tS"
    if (cols.length >= 2 && /^(Sum|Place|S)$/i.test(cols[1])) {
      isFinalFormat = false;
      headerIdx = i;
      break;
    }
    // Generic № header — check next line for judge letters
    headerIdx = i;
    break;
  }

  // For preliminary: find the concatenated judge letters row right after № header
  if (!isFinalFormat) {
    const searchFrom = headerIdx !== -1 ? headerIdx + 1 : dataStartIdx;
    for (let i = searchFrom; i < Math.min(searchFrom + 3, lines.length); i++) {
      const l = lines[i];
      if (/^[A-Z]{2,}$/.test(l)) {
        judgeLettersRow = l.split(""); // "ABCDEFGHI" → ["A","B",...]
        break;
      }
      // Fallback: tab-separated single letters
      const cols = l.split("\t").map(c => c.trim());
      if (cols.length >= 2 && cols.every(c => /^[A-Z]$/.test(c))) {
        judgeLettersRow = cols;
        break;
      }
    }
  }

  // ── Parse competitor rows ──────────────────────────────────────────────────
  const competitors = [];
  // Data starts after the judge letters row (prelim) or after the № header (final)
  let dataIdx = headerIdx !== -1 ? headerIdx + 1 : dataStartIdx;
  if (!isFinalFormat && judgeLettersRow.length > 0) dataIdx++; // skip judge letters row

  for (let i = dataIdx; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    // Skip annotation lines
    if (/^Points?\s+to/i.test(line) || /^Recalled/i.test(line) || /^Not\s+Recalled/i.test(line)) continue;

    const cols = line.split("\t");
    const number = parseInt(cols[0]);
    if (isNaN(number)) continue;

    if (isFinalFormat) {
      // Final: cols = [number, markA, markB, markC, ...]
      const judgeMarks = {};
      for (let j = 0; j < judgeLettersRow.length; j++) {
        const val = parseInt(cols[j + 1]);
        judgeMarks[judgeLettersRow[j]] = isNaN(val) ? null : val;
      }
      competitors.push({ number, crossSum: 0, placeRange: "", judgeRecalls: {}, judgeMarks });
    } else {
      // Preliminary: cols = [number, sum, placeRange, marksStr]
      // Note: placeRange (cols[2]) is the rank within this round only — not the final place
      const crossSum = parseInt(cols[1]) || 0;
      const marksStr = (cols[3] || "").trim().toUpperCase();
      const judgeRecalls = {};
      if (judgeLettersRow.length > 0 && marksStr.length > 0) {
        for (let j = 0; j < judgeLettersRow.length; j++) {
          const mark = marksStr[j] || "-";
          judgeRecalls[judgeLettersRow[j]] = (mark === "X");
        }
      }
      competitors.push({ number, crossSum, placeRange: (cols[2] || "").trim(), judgeRecalls, judgeMarks: {} });
    }
  }

  return { roundLabel, isFinal: isFinalFormat, judgeLetters: judgeLettersRow, competitors };
}

// ─── Merge main data + parsed rounds into a competition document ───────────────
function mergeFlymarkRounds(mainData, parsedRounds, overrideName, overrideCategory, overrideDance, dateStr, multiplier) {
  const { competitionName: parsedName, categoryRaw, danceRaw, adjudicators, participants, results } = mainData;

  const competitionName = overrideName || parsedName || "Competition";
  const rawDance = overrideDance || danceRaw || "";
  const rawCategory = overrideCategory || categoryRaw || "";
  const dance = standardizeDance(rawDance);
  const discipline = detectDiscipline(dance);
  const category = standardizeCategory(rawCategory);

  // Build a map from dancer number → result
  const byNumber = new Map();
  for (const r of results) {
    byNumber.set(r.number, {
      number: r.number,
      dancerName: r.dancerName,
      studio: r.studio,
      place: r.place,
      points: r.points,
      rounds: [],
    });
  }

  // Collect all judge IDs used (union across all rounds + main adjudicators)
  const allJudgeIdsSet = new Set();
  for (const a of adjudicators) allJudgeIdsSet.add(a.id);
  for (const pr of parsedRounds) {
    for (const letter of (pr.judgeLetters || [])) allJudgeIdsSet.add(letter);
  }
  const judgeIds = [...allJudgeIdsSet].sort();

  // For each parsed round, attach round data to each dancer
  for (const pr of parsedRounds) {
    const { roundLabel, isFinal: roundIsFinal, competitors: roundComps } = pr;

    for (const rc of roundComps) {
      const entry = byNumber.get(rc.number);
      if (!entry) continue;

      const alreadyHas = entry.rounds.some(r => r.round === roundLabel);
      if (alreadyHas) continue;

      const danceData = {
        [dance]: {
          type: roundIsFinal ? "final" : "preliminary",
          judgeMarks: rc.judgeMarks || {},
          judgeRecalls: rc.judgeRecalls || {},
          skatingCols: {},
          totalCrosses: rc.crossSum || 0,
          sum: null,
        },
      };

      // For final: rank comes from the main participants table place
      // For preliminary: rank is the cross-sum rank within that round (first number of placeRange)
      let rank;
      if (roundIsFinal) {
        rank = entry.place || 999;
      } else {
        const placeStr = rc.placeRange || "";
        if (/^\d+$/.test(placeStr)) {
          rank = parseInt(placeStr);
        } else if (/^(\d+)\s*[-–]\s*\d+$/.test(placeStr)) {
          rank = parseInt(placeStr.split(/[-–]/)[0].trim());
        } else {
          rank = 999;
        }
      }

      entry.rounds.push({ round: roundLabel, rank, danceData });
    }
  }

  // Synthesise a minimal Final entry only when no Final round was parsed at all.
  // If a Final WAS parsed, only the actual finalists received "F" data above;
  // non-finalists must not receive a spurious empty Final row.
  const hasParsedFinal = parsedRounds.some(pr => pr.isFinal);
  if (!hasParsedFinal) {
    for (const [, entry] of byNumber) {
      if (entry.place > 0 && !entry.rounds.some(r => r.round === "F")) {
        entry.rounds.push({
          round: "F",
          rank: entry.place,
          danceData: {
            [dance]: {
              type: "final",
              judgeMarks: {},
              judgeRecalls: {},
              skatingCols: {},
              totalCrosses: 0,
              sum: null,
            },
          },
        });
      }
    }
  }

  // Sort rounds for each dancer
  for (const [, entry] of byNumber) {
    entry.rounds.sort((a, b) => roundOrder(a.round) - roundOrder(b.round));
  }

  // Determine highestRound for each dancer
  for (const [, entry] of byNumber) {
    if (entry.rounds.length > 0) {
      entry.highestRound = entry.rounds[entry.rounds.length - 1].round;
    } else {
      entry.highestRound = "F";
    }
  }

  // Sort results by place
  const sortedResults = [...byNumber.values()].sort((a, b) => {
    if (a.place === 0 && b.place === 0) return 0;
    if (a.place === 0) return 1;
    if (b.place === 0) return -1;
    return a.place - b.place;
  });

  let timestamp = null;
  if (dateStr) {
    const d = new Date(dateStr + "T00:00:00Z");
    if (!isNaN(d.getTime())) timestamp = Timestamp.fromDate(d);
  }

  const docId = [
    sanitizeId(competitionName).substring(0, 50),
    sanitizeId(category),
    dance ? sanitizeId(dance) : "",
  ].filter(Boolean).join("__");

  return {
    id: docId,
    name: competitionName,
    discipline,
    dance,
    category,
    timestamp,
    multiplier: parseFloat(multiplier) || 1,
    participants,
    adjudicators,
    judgeIds,
    dances: dance ? [dance] : [],
    results: sortedResults.map(r => ({
      dancerName: r.dancerName,
      studio: r.studio || "",
      place: r.place,
      points: r.points,
      number: r.number,
      highestRound: r.highestRound,
      rounds: r.rounds,
    })),
    source: "FlymarkNew",
  };
}

// ─── React Component ───────────────────────────────────────────────────────────
export default function UploadFlymarkNew() {
  const { isAdmin, loading: adminLoading } = useAdmin();

  const [mainText, setMainText] = useState("");
  const [roundInputs, setRoundInputs] = useState([{ id: 0, label: "", text: "" }]);

  const [parsedMain, setParsedMain] = useState(null);
  const [parsedRoundsList, setParsedRoundsList] = useState([]);
  const [mergedDoc, setMergedDoc] = useState(null);
  const [jsonPreview, setJsonPreview] = useState("");

  const [compName, setCompName] = useState("");
  const [competitionDate, setCompetitionDate] = useState("");
  const [overrideCategory, setOverrideCategory] = useState("");
  const [overrideDance, setOverrideDance] = useState("");
  const [multiplier, setMultiplier] = useState("1");
  const [uploadStatus, setUploadStatus] = useState("");

  const [hoveredJudge, setHoveredJudge] = useState(null);
  const [selectedJudges, setSelectedJudges] = useState(new Set());
  const toggleJudge = (jId) => setSelectedJudges(prev => {
    const next = new Set(prev);
    next.has(jId) ? next.delete(jId) : next.add(jId);
    return next;
  });

  if (adminLoading) {
    return <div style={{ padding: "2rem", textAlign: "center" }}><h2>🪰 Upload Flymark New</h2><p>Loading...</p></div>;
  }
  if (!isAdmin) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🪰 Upload Flymark New</h2>
        <div style={{ padding: "2rem", border: "1px solid #f5c6cb", borderRadius: "4px", backgroundColor: "#f8d7da", color: "#721c24", marginTop: "2rem" }}>
          <h3>🔒 Access Denied</h3>
          <p>You need administrator privileges to access this page.</p>
        </div>
      </div>
    );
  }

  const addRound = () => setRoundInputs(prev => [...prev, { id: Date.now(), label: "", text: "" }]);
  const removeRound = (id) => setRoundInputs(prev => prev.filter(r => r.id !== id));
  const updateRound = (id, field, value) => setRoundInputs(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

  const handleParse = () => {
    try {
      if (!mainText.trim()) {
        setUploadStatus("❌ Please paste the main participants section first.");
        return;
      }

      const main = parseFlymarkMain(mainText);
      setParsedMain(main);
      if (!compName && main.competitionName) setCompName(main.competitionName);

      // Assign round labels by position: last round = Final, second-to-last = 1/2 Final, etc.
      const ROUND_SEQUENCE = ["1/32 Final", "1/16 Final", "1/8 Final", "1/4 Final", "1/2 Final", "F"];
      const nonEmptyRounds = roundInputs.filter(r => r.text.trim());
      const positionLabels = ROUND_SEQUENCE.slice(ROUND_SEQUENCE.length - nonEmptyRounds.length);

      const rounds = [];
      const logs = [];
      let posIdx = 0;
      for (const r of roundInputs) {
        if (!r.text.trim()) continue;
        const parsed = parseFlymarkRound(r.text, r.label || null, positionLabels[posIdx++]);
        if (parsed) {
          rounds.push(parsed);
          logs.push(`✅ Round "${parsed.roundLabel}" (${parsed.isFinal ? "Final — numeric marks" : "Preliminary — X/- recalls"}) — ${parsed.competitors.length} competitors, ${parsed.judgeLetters.length} judges (${parsed.judgeLetters.join("")})`);
        }
      }
      setParsedRoundsList(rounds);

      const merged = mergeFlymarkRounds(
        main, rounds,
        compName || main.competitionName,
        overrideCategory, overrideDance, competitionDate, multiplier
      );
      setMergedDoc(merged);
      setJsonPreview(JSON.stringify(merged, null, 2));
      setSelectedJudges(new Set());
      setHoveredJudge(null);

      const mainLog = `✅ Main section: ${main.participants} participants, ${main.adjudicators.length} judges, category: "${main.categoryRaw}", dance: "${main.danceRaw}"`;
      const roundLog = rounds.length > 0 ? "\n" + logs.join("\n") : "\n⚠️ No round detail sections provided — final places only, no per-judge recall data.";
      setUploadStatus(mainLog + roundLog + `\n\n→ Merged: ${merged.participants} participants, doc ID: ${merged.id}`);
    } catch (err) {
      console.error(err);
      setUploadStatus("❌ Parse error: " + err.message);
    }
  };

  const handleUpload = async () => {
    try {
      if (!auth.currentUser) throw new Error("You must be signed in");
      if (!jsonPreview.trim()) throw new Error("Parse the data first");
      if (!competitionDate) throw new Error("Competition date is required");

      const data = JSON.parse(jsonPreview);
      if (!data.results || data.results.length === 0) throw new Error("No results found");

      const d = new Date(competitionDate + "T00:00:00Z");
      data.timestamp = Timestamp.fromDate(d);
      data.multiplier = parseFloat(multiplier) || 1;
      if (compName) data.name = compName;

      data.results = data.results.map(r => ({
        ...r,
        points: calculatePoints(r.place, data.participants),
      }));

      const docId = data.id;
      const docRef = doc(db, "competitions", docId);

      setUploadStatus("⏳ Checking for duplicates...");
      const existingDoc = await getDoc(docRef);
      if (existingDoc.exists()) {
        const existing = existingDoc.data();
        const shouldOverwrite = window.confirm(
          `A competition with this ID already exists:\n\nExisting: "${existing.name}"\nNew: "${data.name}"\n\nOverwrite?`
        );
        if (!shouldOverwrite) {
          setUploadStatus("❌ Upload cancelled — duplicate detected");
          return;
        }
      }

      setUploadStatus("⏳ Uploading to Firebase...");

      const competitionDoc = {
        id: docId,
        name: data.name,
        discipline: data.discipline,
        dance: data.dance,
        category: data.category,
        timestamp: data.timestamp,
        uploadedAt: serverTimestamp(),
        multiplier: data.multiplier,
        participants: data.participants,
        results: data.results.map(r => ({
          dancerName: r.dancerName,
          studio: r.studio || "",
          place: r.place,
          points: r.points,
        })),
        roundData: data.results.map(r => ({
          dancerName: r.dancerName,
          number: r.number || null,
          highestRound: r.highestRound || null,
          rounds: r.rounds || [],
        })),
        adjudicators: data.adjudicators || [],
        judgeIds: data.judgeIds || [],
        dances: data.dances || (data.dance ? [data.dance] : []),
        source: "FlymarkNew",
      };

      await setDoc(docRef, competitionDoc);

      try {
        const storageName = (data.name || "competition").replace(/[\\/:*?"<>|#\[\]]/g, "_").trim();
        const storageCat = (data.category || "category").replace(/[\\/:*?"<>|#\[\]]/g, "_").trim();
        const storageDance = data.dance ? `_${data.dance.replace(/[\\/:*?"<>|#\[\]]/g, "_")}` : "";
        const storageRef = ref(storage, `competition_results/${storageName}/${storageCat}${storageDance}.json`);
        const jsonBlob = new Blob([JSON.stringify(competitionDoc, null, 2)], { type: "application/json" });
        await uploadBytes(storageRef, jsonBlob);
      } catch (storageErr) {
        console.warn("Storage backup failed:", storageErr.message);
      }

      for (const result of data.results || []) {
        if (!result.dancerName) continue;
        await setDoc(doc(db, "dancers", result.dancerName), {
          name: result.dancerName,
          studio: result.studio || "",
          createdAt: serverTimestamp(),
        }, { merge: true });
      }

      await addDoc(collection(db, "competitionLogs"), {
        uploadedBy: auth.currentUser?.email || "Unknown",
        timestamp: serverTimestamp(),
        summary: `${data.name} (${data.participants} participants)`,
        documentPath: `competitions/${docId}`,
        source: "FlymarkNew",
      });

      setUploadStatus(`✅ Upload successful! Document stored at: competitions/${docId}`);
    } catch (err) {
      console.error(err);
      setUploadStatus("❌ Upload failed: " + err.message);
    }
  };

  const ROUND_LABEL_OPTIONS = ["F", "1/2 Final", "1/4 Final", "1/8 Final", "1/16 Final", "1/32 Final"];

  return (
    <div className="upload-container">
      <h2>🪰 Upload Flymark New</h2>
      <p style={{ color: "#666", fontSize: "0.9rem", marginBottom: "1.5rem" }}>
        Paste the main Flymark results page (competition name, judges, participants table).
        Then optionally paste each round's detailed marks tab to store per-judge recall/mark data.
      </p>

      {/* ── Competition metadata ─────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label>🏆 Competition Name (override parsed name):</label>
          <input type="text" placeholder="e.g. 18th Athens Dance Sport Open 2026" value={compName} onChange={e => setCompName(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>📅 Competition Date:</label>
          <input type="date" required value={competitionDate} onChange={e => setCompetitionDate(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>🏷️ Category override (optional):</label>
          <input type="text" placeholder="e.g. Juvenile I" value={overrideCategory} onChange={e => setOverrideCategory(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>💃 Dance override (optional):</label>
          <input type="text" placeholder="e.g. Samba" value={overrideDance} onChange={e => setOverrideDance(e.target.value)} />
        </div>
      </div>

      <div className="form-group" style={{ maxWidth: "200px" }}>
        <label>🧮 Multiplier (default 1):</label>
        <input type="number" step="0.1" min="0.1" className="number-input" value={multiplier} onChange={e => setMultiplier(e.target.value)} />
      </div>

      {/* ── Main participants section ────────────────────────────────────── */}
      <div style={{ marginTop: "1.5rem" }}>
        <h3 style={{ marginBottom: "0.5rem" }}>📋 Main Section (Competition Name + Judges + Participants)</h3>
        <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "0.5rem" }}>
          Copy–paste the full main results page from Flymark (from the competition title down through the participants table).
        </p>
        <textarea
          rows={10}
          className="code-textarea"
          placeholder={"18Th Athens Dance Sport Open\nJuniors 1 Solo - Sa\n\nJudges\nA.\nCarolina Koundoupidou\nGreece\n( 1/8F, F)\n...\n\nEntries: 49 Clubs: 19\n№\tPlace\tParticipant 1\tCity\tClub\tTrainer\n1120\t1\tArdore Sidney\t...\t-\t-"}
          value={mainText}
          onChange={e => setMainText(e.target.value)}
          style={{ width: "100%", fontFamily: "monospace", fontSize: "0.8rem" }}
        />
      </div>

      {/* ── Round detail sections ────────────────────────────────────────── */}
      <div style={{ marginTop: "1.5rem" }}>
        <h3 style={{ marginBottom: "0.5rem" }}>📊 Round Detail Sections (Optional)</h3>
        <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "1rem" }}>
          For each round tab (1/8 Final → Final), paste its detail content. Preliminary rounds show X/- recall marks;
          the Final shows numeric placement marks per judge. Skip any rounds you don't have.
        </p>

        {roundInputs.map((r, idx) => (
          <div key={r.id} style={{ border: "1px solid #ddd", borderRadius: "8px", padding: "1rem", marginBottom: "1rem", backgroundColor: "#fafafa" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.5rem" }}>
              <strong>Round {idx + 1}</strong>
              <select
                value={r.label}
                onChange={e => updateRound(r.id, "label", e.target.value)}
                style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #ccc" }}
              >
                <option value="">Auto-detect from content</option>
                {ROUND_LABEL_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {roundInputs.length > 1 && (
                <button onClick={() => removeRound(r.id)} style={{ marginLeft: "auto", background: "#dc3545", color: "white", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer" }}>
                  ✕ Remove
                </button>
              )}
            </div>
            <textarea
              rows={8}
              className="code-textarea"
              placeholder={idx === 0
                ? "1/8F\n№\tSum\tPlace\tS\nABCDEFGHI\n360\t9\t1 - 9\tXXXXXXXXX\n537\t8\t10 - 13\tXXXXXXXX-\n..."
                : "S\n№\tA\tB\tC\tD\tE\tF\tG\tH\tI\n1120\t3\t6\t4\t1\t2\t1\t1\t1\t6\n..."}
              value={r.text}
              onChange={e => updateRound(r.id, "text", e.target.value)}
              style={{ width: "100%", fontFamily: "monospace", fontSize: "0.8rem" }}
            />
          </div>
        ))}

        <button onClick={addRound} style={{ background: "none", border: "2px dashed #aaa", borderRadius: "8px", padding: "8px 20px", cursor: "pointer", color: "#666", fontSize: "0.9rem", width: "100%", marginBottom: "1rem" }}>
          + Add Another Round
        </button>
      </div>

      <button className="btn btn-primary" onClick={handleParse} style={{ marginRight: "1rem" }}>
        🔍 Parse All Rounds
      </button>

      {/* ── Preview ──────────────────────────────────────────────────────── */}
      {mergedDoc && (
        <div style={{ marginTop: "1.5rem" }}>
          <h3 style={{ marginBottom: "0.75rem" }}>📋 Parsed Data Preview</h3>

          <div style={{ background: "#f0f8ff", border: "1px solid #b8d4e8", borderRadius: "6px", padding: "1rem", marginBottom: "1rem", fontSize: "0.92rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem 1.5rem" }}>
              <div><strong>Competition:</strong> {mergedDoc.name}</div>
              <div><strong>Category:</strong> {mergedDoc.category}</div>
              <div><strong>Discipline:</strong> {mergedDoc.discipline}</div>
              <div><strong>Dance:</strong> {mergedDoc.dance}</div>
              <div><strong>Participants:</strong> {mergedDoc.participants}</div>
              <div><strong>Rounds:</strong> {parsedRoundsList.map(r => r.roundLabel === "F" ? "Final" : r.roundLabel).join(", ") || "Final only"}</div>
              <div style={{ gridColumn: "1 / -1" }}>
                <strong>Adjudicators:</strong>{" "}
                <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                  {(mergedDoc.adjudicators || []).map(a => {
                    const isSelected = selectedJudges.has(a.id);
                    const isHov = hoveredJudge === a.id;
                    const active = isSelected || isHov;
                    return (
                      <span key={`${a.id}-${a.name}`}
                        onMouseEnter={() => setHoveredJudge(a.id)}
                        onMouseLeave={() => setHoveredJudge(null)}
                        onClick={() => toggleJudge(a.id)}
                        style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px", borderRadius: "12px", cursor: "pointer", fontSize: "0.85rem", transition: "all 0.15s", userSelect: "none", background: isSelected ? "#d0e8ff" : isHov ? "#ffe066" : "#e8eaf6", color: active ? "#222" : "#333", fontWeight: active ? "bold" : "normal", boxShadow: isSelected ? "0 0 0 2px #2979ff" : isHov ? "0 0 0 2px #f5c400" : "none", border: "1px solid " + (isSelected ? "#2979ff" : isHov ? "#f5c400" : "#c5cae9") }}>
                        <span style={{ fontWeight: "bold", fontSize: "0.78rem", background: isSelected ? "#2979ff" : isHov ? "#f5c400" : "#3a3a5c", color: isSelected ? "#fff" : isHov ? "#222" : "#fff", borderRadius: "8px", padding: "0 5px", minWidth: "18px", textAlign: "center", display: "inline-block", transition: "all 0.15s" }}>{a.id}</span>
                        {a.name}{a.country ? ` (${a.country})` : ""}{a.rounds?.length > 0 ? ` [${a.rounds.join(", ")}]` : ""}
                      </span>
                    );
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Results table */}
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.4rem" }}>
            <h4 style={{ margin: 0 }}>Results ({mergedDoc.participants} participants)</h4>
            {selectedJudges.size > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "0.85rem", color: "#2979ff" }}>
                <strong>{selectedJudges.size}</strong> judge{selectedJudges.size > 1 ? "s" : ""} selected
                <button onClick={() => setSelectedJudges(new Set())} style={{ background: "none", border: "1px solid #2979ff", color: "#2979ff", borderRadius: "8px", padding: "1px 8px", cursor: "pointer", fontSize: "0.8rem", fontWeight: "bold" }}>✕ Clear</button>
              </span>
            )}
          </div>

          <div style={{ overflowX: "auto", marginBottom: "1.5rem", width: "100%" }}>
            <table style={{ width: "max-content", minWidth: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ background: "#e8e8e8" }}>
                  <th style={thStyle}>Place</th>
                  <th style={thStyle}>No.</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Studio</th>
                  <th style={thStyle}>Rnd.</th>
                  {(mergedDoc.dances || []).map(d => (
                    <React.Fragment key={d}>
                      <th style={{ ...thStyle, background: "#3a3a5c", color: "#fff" }}>
                        {d}<br />
                        <span style={{ fontWeight: "normal", fontSize: "0.76rem", fontFamily: "monospace", display: "inline-flex", gap: 0 }}>
                          {(mergedDoc.judgeIds || []).map(jId => {
                            const adj = (mergedDoc.adjudicators || []).find(a => a.id === jId);
                            const isSelected = selectedJudges.has(jId);
                            const isHov = hoveredJudge === jId;
                            return (
                              <span key={jId}
                                title={adj ? `${adj.id}. ${adj.name}${adj.country ? ` (${adj.country})` : ""}` : jId}
                                onMouseEnter={() => setHoveredJudge(jId)}
                                onMouseLeave={() => setHoveredJudge(null)}
                                onClick={() => toggleJudge(jId)}
                                style={{ cursor: "pointer", width: "1.6ch", display: "inline-block", textAlign: "center", borderRadius: "2px", userSelect: "none", color: isSelected ? "#7ec8ff" : isHov ? "#ffe066" : "#fff", background: isSelected ? "rgba(41,121,255,0.25)" : isHov ? "rgba(255,224,102,0.18)" : "transparent", fontWeight: (isSelected || isHov) ? "bold" : "normal", transition: "all 0.1s" }}>
                                {jId}
                              </span>
                            );
                          })}
                        </span>
                      </th>
                      <th style={{ ...thStyle, textAlign: "center", padding: "2px 4px", background: "#3a3a5c", color: "#fff" }}>S</th>
                    </React.Fragment>
                  ))}
                  <th style={thStyle}>Points</th>
                </tr>
              </thead>
              <tbody>
                {(mergedDoc.results || []).map((r, idx) => {
                  const bgBase = idx % 2 === 0 ? "#fff" : "#fafafa";
                  const jIds = mergedDoc.judgeIds || [];
                  // Sort rounds latest first so Final appears at top
                  const rounds = [...(r.rounds || [])].sort((a, b) => roundOrder(b.round) - roundOrder(a.round));
                  return rounds.map((rd, rdIdx) => {
                    const isFirstRound = rdIdx === 0;
                    const isFinalRound = rd.round === "F";
                    const rowBg = isFinalRound ? "#f0fff0" : bgBase;
                    return (
                      <tr key={`${idx}-${rdIdx}`} style={{ background: rowBg, borderTop: isFirstRound ? "2px solid #ccc" : undefined }}>
                        {isFirstRound && (
                          <>
                            <td style={{ ...tdStyle, fontWeight: "bold" }} rowSpan={rounds.length}>{r.place || "—"}</td>
                            <td style={tdStyle} rowSpan={rounds.length}>{r.number || "—"}</td>
                            <td style={tdStyle} rowSpan={rounds.length}><strong>{r.dancerName}</strong></td>
                            <td style={{ ...tdStyle, fontSize: "0.78rem", color: "#555" }} rowSpan={rounds.length}>{r.studio || "—"}</td>
                          </>
                        )}
                        <td style={{ ...tdStyle, fontWeight: isFinalRound ? "bold" : "normal", color: isFinalRound ? "#1a7a1a" : "#555", background: rowBg }}>
                          {rd.round === "F" ? "Final" : rd.round}
                        </td>
                        {(mergedDoc.dances || []).map(d => {
                          const dd = rd.danceData?.[d];
                          if (!dd) return (
                            <React.Fragment key={d}>
                              <td style={{ ...tdStyle, color: "#bbb", background: rowBg }}>—</td>
                              <td style={{ ...tdStyle, color: "#bbb", background: rowBg }}>—</td>
                            </React.Fragment>
                          );
                          const isFinal = dd.type === "final";
                          const sumDisplay = isFinal ? "" : (dd.totalCrosses != null ? String(dd.totalCrosses) : "");
                          return (
                            <React.Fragment key={d}>
                              <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: "0.76rem", background: rowBg, whiteSpace: "pre", padding: "4px 6px" }}>
                                {jIds.map(jId => {
                                  let mark;
                                  if (isFinal) {
                                    const m = dd.judgeMarks?.[jId];
                                    mark = m != null ? String(m) : "·";
                                  } else {
                                    const rc = dd.judgeRecalls?.[jId];
                                    mark = rc === true ? "x" : "·";
                                  }
                                  const isHov = hoveredJudge === jId;
                                  const isSel = selectedJudges.has(jId);
                                  let bg = "transparent", fg = "inherit";
                                  if (isHov && isSel) { bg = "#ffb347"; fg = "#111"; }
                                  else if (isSel)     { bg = "#bbdefb"; fg = "#0d47a1"; }
                                  else if (isHov)     { bg = "#ffe066"; fg = "#222"; }
                                  return (
                                    <span key={jId}
                                      onMouseEnter={() => setHoveredJudge(jId)}
                                      onMouseLeave={() => setHoveredJudge(null)}
                                      onClick={() => toggleJudge(jId)}
                                      title={(mergedDoc.adjudicators || []).find(a => a.id === jId)?.name || jId}
                                      style={{ display: "inline-block", width: "1.6ch", textAlign: "center", background: bg, color: fg, borderRadius: "2px", fontWeight: (isHov || isSel) ? "bold" : "normal", transition: "background 0.1s", cursor: "pointer", userSelect: "none" }}>
                                      {mark}
                                    </span>
                                  );
                                })}
                              </td>
                              <td style={{ ...tdStyle, fontFamily: "monospace", fontWeight: "bold", background: rowBg, textAlign: "center", padding: "2px 4px" }}>
                                {sumDisplay}
                              </td>
                            </React.Fragment>
                          );
                        })}
                        {isFirstRound && <td style={tdStyle} rowSpan={rounds.length}>{r.points}</td>}
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>

          <div className="form-group">
            <label>📝 JSON (editable before upload):</label>
            <textarea rows={16} className="code-textarea" value={jsonPreview} onChange={e => setJsonPreview(e.target.value)} style={{ fontFamily: "monospace", fontSize: "0.78rem" }} />
          </div>
        </div>
      )}

      {jsonPreview && (
        <button className="btn btn-success btn-upload" onClick={handleUpload} style={{ marginTop: "1rem" }}>
          ☁️ Upload to Firebase
        </button>
      )}

      {uploadStatus && (
        <div className="upload-status" style={{ whiteSpace: "pre-wrap", marginTop: "1rem" }}>
          {uploadStatus}
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: "5px 8px", border: "1px solid #ccc", textAlign: "left", fontWeight: "600", whiteSpace: "nowrap" };
const tdStyle = { padding: "4px 8px", border: "1px solid #e0e0e0", whiteSpace: "nowrap" };